import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Eye,
  AlertTriangle,
  RefreshCw,
  Activity,
  BarChart3,
  Newspaper,
  MessageSquare,
  Globe,
  Zap
} from 'lucide-react';
import { getAITradingService } from '../../services/AITradingService';
import { SentimentAnalysis, MarketPattern } from '../../types/ai-trading';

interface MarketInsight {
  id: string;
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  timeframe: string;
  category: 'technical' | 'fundamental' | 'sentiment' | 'onchain';
  actionable: boolean;
}

interface TechnicalAnalysis {
  token: string;
  indicators: {
    rsi: number;
    macd: number;
    sma20: number;
    sma50: number;
    volume: number;
    volatility: number;
  };
  support: number[];
  resistance: number[];
  patterns: MarketPattern[];
}

export function MarketAnalysisAI() {
  const [selectedToken, setSelectedToken] = useState('ETH');
  const [sentiment, setSentiment] = useState<SentimentAnalysis | null>(null);
  const [insights, setInsights] = useState<MarketInsight[]>([]);
  const [technicalData, setTechnicalData] = useState<TechnicalAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [marketOverview, setMarketOverview] = useState({
    fearGreedIndex: 72,
    totalMarketCap: 2.3e12,
    dominance: { btc: 52.4, eth: 17.8 },
    volatilityIndex: 28.5
  });

  const tokens = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'LINK', name: 'Chainlink' },
    { symbol: 'UNI', name: 'Uniswap' },
    { symbol: 'AAVE', name: 'Aave' }
  ];

  useEffect(() => {
    loadMarketAnalysis();
  }, [selectedToken]);

  const loadMarketAnalysis = async () => {
    try {
      setLoading(true);

      // Load sentiment analysis
      const sentimentData = await getAITradingService().getMarketSentiment(selectedToken);
      setSentiment(sentimentData);

      // Generate market insights
      const newInsights: MarketInsight[] = [
        {
          id: '1',
          title: 'Strong Institutional Accumulation Detected',
          description: 'Large wallet addresses have been accumulating significant amounts over the past 7 days, indicating institutional interest.',
          confidence: 87,
          impact: 'high',
          timeframe: '1-2 weeks',
          category: 'onchain',
          actionable: true
        },
        {
          id: '2',
          title: 'Technical Breakout Imminent',
          description: 'Price is approaching a critical resistance level with high volume, suggesting a potential breakout.',
          confidence: 73,
          impact: 'medium',
          timeframe: '2-5 days',
          category: 'technical',
          actionable: true
        },
        {
          id: '3',
          title: 'Regulatory News Impact',
          description: 'Recent regulatory developments may create short-term volatility but long-term positive sentiment.',
          confidence: 65,
          impact: 'medium',
          timeframe: '1 week',
          category: 'fundamental',
          actionable: false
        },
        {
          id: '4',
          title: 'Social Sentiment Surge',
          description: 'Positive mentions across social platforms have increased by 340% in the last 24 hours.',
          confidence: 91,
          impact: 'high',
          timeframe: '1-3 days',
          category: 'sentiment',
          actionable: true
        }
      ];
      setInsights(newInsights);

      // Generate technical analysis
      const technical: TechnicalAnalysis = {
        token: selectedToken,
        indicators: {
          rsi: 67.3,
          macd: 12.45,
          sma20: 2845.67,
          sma50: 2720.33,
          volume: 24567890,
          volatility: 0.034
        },
        support: [2650, 2580, 2420],
        resistance: [2950, 3120, 3280],
        patterns: [
          {
            id: 'pattern1',
            name: 'Ascending Triangle',
            description: 'Bullish continuation pattern forming over 2 weeks',
            confidence: 82,
            timeframe: '1-2 weeks',
            expectedMove: 8.5,
            riskReward: 1.8,
            historicalAccuracy: 74
          },
          {
            id: 'pattern2',
            name: 'Volume Accumulation',
            description: 'Increasing volume with price consolidation',
            confidence: 75,
            timeframe: '3-7 days',
            expectedMove: 5.2,
            riskReward: 2.1,
            historicalAccuracy: 68
          }
        ]
      };
      setTechnicalData(technical);

    } catch (error) {
      console.error('Error loading market analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (value: number) => {
    if (value > 0.3) return 'text-green-500';
    if (value < -0.3) return 'text-red-500';
    return 'text-yellow-500';
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'technical': return <BarChart3 className="h-4 w-4" />;
      case 'fundamental': return <Globe className="h-4 w-4" />;
      case 'sentiment': return <MessageSquare className="h-4 w-4" />;
      case 'onchain': return <Activity className="h-4 w-4" />;
      default: return <Eye className="h-4 w-4" />;
    }
  };

  // Sample data for charts
  const sentimentHistoryData = Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
    sentiment: Math.random() * 2 - 1,
    volume: Math.random() * 1000000
  }));

  const marketMetricsData = [
    { name: 'Fear & Greed', value: marketOverview.fearGreedIndex, color: '#8884d8' },
    { name: 'Volatility', value: marketOverview.volatilityIndex, color: '#82ca9d' },
    { name: 'BTC Dominance', value: marketOverview.dominance.btc, color: '#ffc658' },
    { name: 'ETH Dominance', value: marketOverview.dominance.eth, color: '#ff7300' }
  ];

  const radarData = technicalData ? [
    { indicator: 'RSI', value: technicalData.indicators.rsi, fullMark: 100 },
    { indicator: 'Volume', value: 75, fullMark: 100 },
    { indicator: 'Momentum', value: 68, fullMark: 100 },
    { indicator: 'Trend', value: 82, fullMark: 100 },
    { indicator: 'Support', value: 71, fullMark: 100 },
    { indicator: 'Volatility', value: technicalData.indicators.volatility * 1000, fullMark: 100 }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
            <Brain className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1>AI Market Analysis</h1>
            <p className="text-muted-foreground">
              Advanced market insights powered by machine learning
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={selectedToken}
            onChange={(e) => setSelectedToken(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            {tokens.map((token) => (
              <option key={token.symbol} value={token.symbol}>
                {token.symbol} - {token.name}
              </option>
            ))}
          </select>

          <Button
            onClick={loadMarketAnalysis}
            disabled={loading}
            variant="outline"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Market Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fear & Greed Index</p>
                <p className="text-2xl font-bold text-green-500">
                  {marketOverview.fearGreedIndex}
                </p>
                <p className="text-xs text-muted-foreground">Greed</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Market Cap</p>
                <p className="text-2xl font-bold">
                  ${(marketOverview.totalMarketCap / 1e12).toFixed(1)}T
                </p>
                <p className="text-xs text-green-500">+2.4%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">BTC Dominance</p>
                <p className="text-2xl font-bold">{marketOverview.dominance.btc}%</p>
                <p className="text-xs text-red-500">-0.8%</p>
              </div>
              <Activity className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Volatility Index</p>
                <p className="text-2xl font-bold">{marketOverview.volatilityIndex}</p>
                <p className="text-xs text-yellow-500">Moderate</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Analysis */}
      <Tabs defaultValue="sentiment" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="sentiment">Sentiment Analysis</TabsTrigger>
          <TabsTrigger value="technical">Technical Analysis</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="patterns">Pattern Recognition</TabsTrigger>
        </TabsList>

        <TabsContent value="sentiment" className="space-y-4">
          {sentiment && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Overall Sentiment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center space-y-4">
                      <div className={`text-4xl font-bold ${getSentimentColor(sentiment.overall)}`}>
                        {(sentiment.overall * 100).toFixed(0)}
                      </div>
                      <Progress 
                        value={(sentiment.overall + 1) * 50} 
                        className="w-full"
                      />
                      <p className="text-sm text-muted-foreground">
                        {sentiment.overall > 0.3 ? 'Very Bullish' :
                         sentiment.overall > 0 ? 'Bullish' :
                         sentiment.overall > -0.3 ? 'Bearish' : 'Very Bearish'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Sentiment Sources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <MessageSquare className="h-4 w-4" />
                          <span>Social Media</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={getSentimentColor(sentiment.sources.social)}>
                            {(sentiment.sources.social * 100).toFixed(0)}
                          </span>
                          <Progress value={(sentiment.sources.social + 1) * 50} className="w-20" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Newspaper className="h-4 w-4" />
                          <span>News</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={getSentimentColor(sentiment.sources.news)}>
                            {(sentiment.sources.news * 100).toFixed(0)}
                          </span>
                          <Progress value={(sentiment.sources.news + 1) * 50} className="w-20" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <BarChart3 className="h-4 w-4" />
                          <span>Technical</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={getSentimentColor(sentiment.sources.technical)}>
                            {(sentiment.sources.technical * 100).toFixed(0)}
                          </span>
                          <Progress value={(sentiment.sources.technical + 1) * 50} className="w-20" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Activity className="h-4 w-4" />
                          <span>On-Chain</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={getSentimentColor(sentiment.sources.onchain)}>
                            {(sentiment.sources.onchain * 100).toFixed(0)}
                          </span>
                          <Progress value={(sentiment.sources.onchain + 1) * 50} className="w-20" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Sentiment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={sentimentHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[-1, 1]} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="sentiment"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Trending Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {sentiment.keywords.map((keyword, index) => (
                      <Badge key={index} variant="secondary">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="technical" className="space-y-4">
          {technicalData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Technical Indicators</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">RSI</p>
                          <p className="text-lg font-semibold">
                            {technicalData.indicators.rsi.toFixed(1)}
                          </p>
                          <Progress value={technicalData.indicators.rsi} className="mt-1" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">MACD</p>
                          <p className={`text-lg font-semibold ${
                            technicalData.indicators.macd > 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {technicalData.indicators.macd.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">SMA 20</p>
                          <p className="text-lg font-semibold">
                            ${technicalData.indicators.sma20.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">SMA 50</p>
                          <p className="text-lg font-semibold">
                            ${technicalData.indicators.sma50.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground">24h Volume</p>
                        <p className="text-lg font-semibold">
                          ${technicalData.indicators.volume.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Technical Radar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="indicator" />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} />
                        <Radar
                          name="Technical Score"
                          dataKey="value"
                          stroke="#8884d8"
                          fill="#8884d8"
                          fillOpacity={0.3}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Support Levels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {technicalData.support.map((level, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm">Support {index + 1}</span>
                          <span className="font-semibold text-green-500">
                            ${level.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Resistance Levels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {technicalData.resistance.map((level, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm">Resistance {index + 1}</span>
                          <span className="font-semibold text-red-500">
                            ${level.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {insights.map((insight) => (
              <Card key={insight.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getCategoryIcon(insight.category)}
                      <div>
                        <h3 className="font-semibold">{insight.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {insight.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {insight.actionable && (
                        <Badge variant="default">
                          <Zap className="h-3 w-3 mr-1" />
                          Actionable
                        </Badge>
                      )}
                      <Badge 
                        variant="outline"
                        className={getImpactColor(insight.impact)}
                      >
                        {insight.impact} Impact
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Confidence: </span>
                        <span className="font-semibold">{insight.confidence}%</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Timeframe: </span>
                        <span className="font-semibold">{insight.timeframe}</span>
                      </div>
                    </div>
                    
                    <Progress value={insight.confidence} className="w-32" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          {technicalData?.patterns && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {technicalData.patterns.map((pattern) => (
                <Card key={pattern.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{pattern.name}</span>
                      <Badge variant="outline">
                        {pattern.confidence}% confidence
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        {pattern.description}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Expected Move</p>
                          <p className="text-lg font-semibold text-green-500">
                            +{pattern.expectedMove}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Risk/Reward</p>
                          <p className="text-lg font-semibold">
                            1:{pattern.riskReward}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Historical Accuracy: </span>
                          <span className="font-semibold">{pattern.historicalAccuracy}%</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Timeframe: </span>
                          <span className="font-semibold">{pattern.timeframe}</span>
                        </div>
                      </div>

                      <Progress value={pattern.historicalAccuracy} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}